import {
  createContext,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { emptyFunction } from '../../../../utils/emptyFunction';
import { Confirm } from '../components/Confirm';

export interface ConfirmOptions {
  cancelLabel?: string;
  confirmLabel?: string;
  message: string;
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
}

export interface ConfirmContext {
  openConfirm: (c: ConfirmOptions) => void;
}

export const ConfirmContext = createContext<ConfirmContext>({
  openConfirm: emptyFunction,
});

export const ConfirmContextProvider = ({ children }: PropsWithChildren) => {
  const [confirmOptions, setConfirmOptions] = useState<
    ConfirmOptions | undefined
  >();

  const openConfirm = useCallback((newConfirmOptions: ConfirmOptions) => {
    setConfirmOptions(newConfirmOptions);
  }, []);

  const handleCancel = useCallback(() => {
    if (!confirmOptions) {
      return;
    }

    confirmOptions.onCancel();
    setConfirmOptions(undefined);
  }, [confirmOptions]);

  const handleConfirm = useCallback(() => {
    if (!confirmOptions) {
      return;
    }

    confirmOptions.onConfirm();
    setConfirmOptions(undefined);
  }, [confirmOptions]);

  const value = useMemo(() => ({ openConfirm }), [openConfirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {!!confirmOptions && (
        <Confirm
          message={confirmOptions.message}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
          title={confirmOptions.title}
        />
      )}
      {children}
    </ConfirmContext.Provider>
  );
};
